import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { config as appConfig } from '../config/app-config';

declare global {
  interface Window {
    /**
     * CloudWatch RUM web client. `@amzn/xb-lite` reads this at logger-creation
     * time (duck-typing for recordEvent/recordError/recordPageView) to emit
     * Experience Bridge availability metrics. If it is not set, xb-lite falls
     * back to a silent no-op logger.
     */
    cwr?: AwsRum;
  }
}

let awsRum: AwsRum | undefined;

export const configureAwsRum = (): AwsRum | undefined => {
  if (awsRum || !appConfig.rum.applicationId || !appConfig.rum.identityPoolId || !appConfig.rum.guestRoleArn) {
    return awsRum;
  }
  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      guestRoleArn: appConfig.rum.guestRoleArn,
      identityPoolId: appConfig.rum.identityPoolId,
      endpoint: `https://dataplane.rum.${appConfig.rum.applicationRegion}.amazonaws.com`,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: false,
      enableXRay: false,
    };

    const APPLICATION_ID = appConfig.rum.applicationId;
    const APPLICATION_VERSION = '1.0.0';
    const APPLICATION_REGION = appConfig.rum.applicationRegion;

    awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);

    // Expose the client on window.cwr so @amzn/xb-lite can emit XB events
    // through it. Without this, xb-lite's RUM logger is a silent no-op.
    window.cwr = awsRum;
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
  return awsRum;
};

/**
 * Accessor for the CloudWatch RUM web client singleton. Consumers (e.g. the
 * Engagement_Tracker) resolve the client through this getter and never
 * construct an AwsRum instance themselves.
 */
export const getAwsRum = (): AwsRum | undefined => awsRum;

/**
 * Toggle RUM cookie usage at runtime to match cookie consent. Called when the
 * consent state changes: enable on grant, disable otherwise. No-op if the RUM
 * web client has not been initialized.
 */
export const applyRumCookieConsent = (granted: boolean): void => {
  awsRum?.allowCookies(granted);
};
